import React, { useState, useEffect, useMemo } from 'react';
import { Product, ProductVariant } from '../../shared/types';
import { useCart } from '../../shared/context/CartContext';
import { useToast } from '../../shared/hooks/useToast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { formatCurrency } from '../../shared/utils/currency';
import LuxuryImageGallery from './LuxuryImageGallery';
import api from '../../shared/services/api';
import ModalSkeleton from './skeletons/ModalSkeleton';
import ProductDetailSkeleton from './skeletons/ProductDetailSkeleton';
import { trackAddToCart } from '../../shared/utils/metaPixel';

interface QuickAddModalProps {
  product: Product | null;
  onClose: () => void;
  onBuyNow?: boolean;
}

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing.md};
`;

const Modal = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${props => props.theme.colors.background.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  z-index: 10;
  transition: background-color ${props => props.theme.transitions.base};
  
  &:hover {
    background: ${props => props.theme.colors.gray[300]};
  }
`;

const ModalContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const ImageGalleryWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
  
  & > div > div:first-child {
    aspect-ratio: auto !important;
  }
  
  & img {
    object-fit: contain !important;
  }
  
  [data-rmiz-ghost] {
    display: none !important;
  }
`;

const ProductTitle = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.primary};
`;

const ProductPrice = styled.div`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const ProductDescription = styled.div`
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.base};
  color: ${props => props.theme.colors.text.secondary};
  line-height: ${props => props.theme.typography.lineHeight.relaxed};
  margin-bottom: ${props => props.theme.spacing.lg};
  max-height: 200px;
  overflow-y: auto;
  
  /* HTML content styles */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    margin-top: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.xs};
    color: ${props => props.theme.colors.text.primary};
  }
  
  p {
    margin-bottom: ${props => props.theme.spacing.sm};
  }
  
  ul, ol {
    margin-left: ${props => props.theme.spacing.md};
    margin-bottom: ${props => props.theme.spacing.sm};
  }
  
  li {
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  table {
    width: 100%;
    border-collapse: collapse;
    margin: ${props => props.theme.spacing.sm} 0;
    font-size: ${props => props.theme.typography.fontSize.sm};
    
    th, td {
      border: 1px solid ${props => props.theme.colors.gray[300] || '#D4D4D4'};
      padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
      text-align: left;
    }
    
    th {
      background-color: ${props => props.theme.colors.background.secondary || props.theme.colors.gray[100] || '#F3F4F6'};
      font-weight: ${props => props.theme.typography.fontWeight.semibold};
      color: ${props => props.theme.colors.text.primary};
    }
    
    tr:nth-child(even) {
      background-color: ${props => props.theme.colors.background.secondary || props.theme.colors.gray[50] || '#F9FAFB'};
    }
  }
  
  a {
    color: ${props => props.theme.colors.text.primary};
    text-decoration: underline;
    
    &:hover {
      text-decoration: none;
    }
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.sm};
    margin: ${props => props.theme.spacing.sm} 0;
  }
  
  /* Responsive tables */
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    table {
      font-size: ${props => props.theme.typography.fontSize.xs};
      display: block;
      overflow-x: auto;
      white-space: nowrap;
    }
    
    th, td {
      padding: ${props => props.theme.spacing.xs};
    }
  }
`;

const VariantSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const VariantLabel = styled.label`
  display: block;
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text.primary};
`;

const VariantOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.sm};
`;

const ColorSwatch = styled.button<{ $color: string; $selected: boolean; $disabled: boolean; $hasImage?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.$hasImage ? '8px' : '50%'};
  border: 1px solid ${props => {
    if (props.$selected) {
      return props.theme.colors.text.primary;
    }
    return props.theme.colors.gray[300] || '#D4D4D4';
  }};
  background-color: ${props => props.$hasImage ? 'transparent' : props.$color};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all ${props => props.theme.transitions.base};
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.$selected ? `0 0 0 2px ${props.theme.colors.background.primary}, 0 0 0 3px ${props.theme.colors.text.primary}` : 'none'};
  
  ${props => props.$disabled && `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 150%;
      height: 2.5px;
      background-color: ${props.theme.colors.error || '#DC2626'};
      transform: translate(-50%, -50%) rotate(45deg);
      transform-origin: center;
      z-index: 2;
      box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
    }
  `}
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.text.primary};
    transform: ${props => props.$hasImage ? 'scale(1.05)' : 'none'};
  }
`;

const SizeOption = styled.button<{ $selected: boolean; $disabled: boolean; $hasImage?: boolean }>`
  padding: ${props => props.$hasImage ? '0' : `${props.theme.spacing.xs} ${props.theme.spacing.md}`};
  ${props => props.$hasImage && `
    width: 40px;
    height: 40px;
    border-radius: 8px;
  `}
  border: 1px solid ${props => 
    props.$selected 
      ? props.theme.colors.text.primary 
      : props.theme.colors.gray[300]
  };
  background-color: ${props => 
    props.$hasImage 
      ? 'transparent'
      : (props.$selected 
          ? props.theme.colors.text.primary 
          : 'transparent')
  };
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: ${props => 
    props.$hasImage
      ? 'transparent'
      : (props.$selected 
          ? props.theme.colors.white 
          : props.theme.colors.text.primary)
  };
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.sm};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all ${props => props.theme.transitions.base};
  position: relative;
  overflow: hidden;
  border-radius: ${props => props.$hasImage ? '8px' : props.theme.borderRadius.sm};
  
  ${props => props.$disabled && `
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: -10%;
      right: -10%;
      height: 2.5px;
      background-color: ${props.theme.colors.error || '#DC2626'};
      transform: translateY(-50%) rotate(45deg);
      transform-origin: center;
      z-index: 2;
      box-shadow: 0 0 3px rgba(0, 0, 0, 0.5);
    }
  `}
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.text.primary};
    transform: ${props => props.$hasImage ? 'scale(1.05)' : 'none'};
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const QuantityLabel = styled.label`
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${props => props.theme.colors.gray[300]};
  border-radius: ${props => props.theme.borderRadius.sm};
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.lg};
  color: ${props => props.theme.colors.text.primary};
  transition: background-color ${props => props.theme.transitions.base};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.background.secondary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  height: 36px;
  border: none;
  border-left: 1px solid ${props => props.theme.colors.gray[300]};
  border-right: 1px solid ${props => props.theme.colors.gray[300]};
  text-align: center;
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.base};
  
  &:focus {
    outline: none;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-direction: column;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  padding: ${props => props.theme.spacing.md};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.body};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.base};
  
  ${props => props.$variant === 'primary' ? `
    background-color: ${props.theme.colors.text.primary};
    color: ${props.theme.colors.white};
    
    &:hover:not(:disabled) {
      opacity: 0.9;
    }
  ` : `
    background-color: transparent;
    color: ${props.theme.colors.text.primary};
    border: 1px solid ${props.theme.colors.text.primary};
    
    &:hover:not(:disabled) {
      background-color: ${props.theme.colors.text.primary};
      color: ${props.theme.colors.white};
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StockStatus = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const QuickAddModal: React.FC<QuickAddModalProps> = ({ product, onClose, onBuyNow = false }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { showError, showWarning, showSuccess } = useToast();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [fullProduct, setFullProduct] = useState<Product | null>(product);
  const [isLoadingFullProduct, setIsLoadingFullProduct] = useState(false);

  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [product]);

  // Fetch full product data if needed (check if description, all images, or variants are missing)
  useEffect(() => {
    const fetchFullProduct = async () => {
      if (!product || isLoadingFullProduct) return;
      
      // Check if we need to fetch full product data
      const needsFullData = 
        !product.description || 
        !product.variants || 
        product.variants.length === 0 ||
        (product.imageUrls && product.imageUrls.length <= 2); // If only 2 images, might be from homepage
      
      if (needsFullData) {
        setIsLoadingFullProduct(true);
        try {
          // Try to fetch by handle first, then by ID
          let fullProductData: Product;
          if (product.handle) {
            try {
              fullProductData = (await api.get<Product>(`/products/handle/${product.handle}`)).data;
            } catch {
              fullProductData = (await api.get<Product>(`/products/${product.id}`)).data;
            }
          } else {
            fullProductData = (await api.get<Product>(`/products/${product.id}`)).data;
          }
          
          setFullProduct(fullProductData);
        } catch (error) {
          console.error('Failed to fetch full product data:', error);
          // Keep using the original product data if fetch fails
          setFullProduct(product);
        } finally {
          setIsLoadingFullProduct(false);
        }
      } else {
        setFullProduct(product);
      }
    };

    fetchFullProduct();
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fullProduct || !fullProduct.variants || fullProduct.variants.length === 0) {
      setSelectedVariant(null);
      return;
    }

    const variant = fullProduct.variants.find(
      v => v.size === selectedSize
    );

    setSelectedVariant(variant || null);
  }, [selectedSize, fullProduct]);

  const hasVariants = fullProduct?.variants && fullProduct.variants.length > 0;
  
  const stockQuantity = selectedVariant ? selectedVariant.stockQuantity : (fullProduct?.variants?.reduce((sum, v) => sum + (v.stockQuantity || 0), 0) || 0);
  const isInStock = stockQuantity > 0;
  const canPurchase = isInStock && (!hasVariants || !!selectedSize);

  const colorMap: Record<string, string> = {
    'red': '#ef4444',
    'blue': '#3b82f6',
    'green': '#10b981',
    'yellow': '#fbbf24',
    'black': '#1f2937',
    'white': '#ffffff',
    'gray': '#6b7280',
    'grey': '#6b7280',
    'pink': '#ec4899',
    'purple': '#a855f7',
    'orange': '#f97316',
  };

  const handleAddToCart = async () => {
    if (!fullProduct) return;
    
    if (hasVariants && !selectedSize) {
      showWarning('Please select a size before adding to cart');
      return;
    }
    
    try {
      await addToCart(fullProduct.id, selectedVariant?.id, quantity);
      
      trackAddToCart({
        id: fullProduct.id,
        title: fullProduct.title,
        price: displayPrice,
        quantity: quantity,
        variantId: selectedVariant?.id,
      });
      showSuccess(t('toasts.addedToCart'));
      onClose();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showError('Failed to add item to cart');
    }
  };

  const handleBuyNow = async () => {
    if (!fullProduct) return;
    
    if (hasVariants && !selectedSize) {
      showWarning('Please select a size before purchasing');
      return;
    }
    
    try {
      await addToCart(fullProduct.id, selectedVariant?.id, quantity);
      navigate('/checkout');
      onClose();
    } catch (error) {
      console.error('Failed to add to cart:', error);
      showError('Failed to add item to cart');
    }
  };

  // Get images: use variant images if available, otherwise product images (same logic as ProductDetailPage)
  // Must be before early return to follow rules of hooks
  const displayImages = useMemo(() => {
    if (!fullProduct) return [];
    // Always use product images as variant images are removed
    return fullProduct?.imageUrls || [];
  }, [fullProduct?.imageUrls, fullProduct]);
  
  // Get videos from product
  const displayVideos = fullProduct?.videoUrls || [];
  
  const displayPrice = selectedVariant?.priceOverride ?? fullProduct?.price ?? 0;

  if (isLoadingFullProduct || !fullProduct) {
    return (
      <Overlay
        key="modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose}>×</CloseButton>
          <ModalSkeleton>
            <ProductDetailSkeleton />
          </ModalSkeleton>
        </Modal>
      </Overlay>
    );
  }

  return (
    <Overlay
      key="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <Modal
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
          <CloseButton onClick={onClose}>×</CloseButton>
          <ModalContent>
            <ImageGalleryWrapper>
              <LuxuryImageGallery 
                key={selectedVariant?.id || fullProduct?.id || 'gallery'}
                images={displayImages.length > 0 ? displayImages : ['/placeholder.jpg']}
                videos={displayVideos}
                alt={fullProduct.title}
              />
            </ImageGalleryWrapper>
            <ProductTitle>{fullProduct.title}</ProductTitle>
            <ProductPrice>{formatCurrency(displayPrice)}</ProductPrice>
            
            {fullProduct.description && (
              <ProductDescription dangerouslySetInnerHTML={{ __html: fullProduct.description }} />
            )}

            {hasVariants && (
              <VariantSection>
                <VariantLabel>Size:</VariantLabel>
                <VariantOptions>
                  {fullProduct?.variants?.map((variant) => {
                    const isSelected = selectedSize === variant.size;
                    const isAvailable = variant.stockQuantity > 0;
                    
                    return (
                      <SizeOption
                        key={variant.id}
                        $selected={isSelected}
                        $disabled={!isAvailable}
                        onClick={() => {
                          if (isAvailable) {
                            setSelectedSize(variant.size);
                          }
                        }}
                      >
                        {variant.size}
                      </SizeOption>
                    );
                  })}
                </VariantOptions>
              </VariantSection>
            )}

            <StockStatus>
              {selectedVariant 
                ? (isInStock ? `In Stock (${stockQuantity} available)` : 'Out of Stock')
                : (isInStock ? 'In Stock' : 'Out of Stock')
              }
            </StockStatus>

            <QuantitySelector>
              <QuantityLabel>Quantity:</QuantityLabel>
              <QuantityControls>
                <QuantityButton
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  −
                </QuantityButton>
                <QuantityInput
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setQuantity(Math.max(1, Math.min(val, stockQuantity || 999)));
                  }}
                />
                <QuantityButton
                  onClick={() => setQuantity(quantity + 1)}
                  disabled={quantity >= (stockQuantity || 999)}
                >
                  +
                </QuantityButton>
              </QuantityControls>
            </QuantitySelector>

            <ButtonGroup>
              <ActionButton
                $variant="primary"
                onClick={onBuyNow ? handleBuyNow : handleAddToCart}
                disabled={!canPurchase}
              >
                {onBuyNow ? 'Buy Now' : 'Add to Cart'}
              </ActionButton>
              {!onBuyNow && (
                <ActionButton
                  $variant="secondary"
                  onClick={handleBuyNow}
                  disabled={!canPurchase}
                >
                  Buy Now
                </ActionButton>
              )}
            </ButtonGroup>
          </ModalContent>
        </Modal>
      </Overlay>
  );
};

export default QuickAddModal;

